'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SleepCalculator() {
  const t = useTranslations("tools.sleepCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Sleep Cycle Calculator State
  const [cycleMode, setCycleMode] = useState<'wakeup' | 'bedtime'>('wakeup');
  const [cycleHour, setCycleHour] = useState(6);
  const [cycleMin, setCycleMin] = useState(0);
  const [cycleAmpm, setCycleAmpm] = useState<'AM' | 'PM'>('AM');
  const [cycleLength, setCycleLength] = useState(90);
  const [fallAsleep, setFallAsleep] = useState(15);
  const [showCycleSettings, setShowCycleSettings] = useState(false);
  const [cycleResults, setCycleResults] = useState<Array<{cycles: number, time: string, recommended: boolean}> | null>(null);

  // Duration Calculator State
  const [durationMode, setDurationMode] = useState<'wakeup' | 'bedtime'>('wakeup');
  const [durationHour, setDurationHour] = useState(6);
  const [durationMin, setDurationMin] = useState(0);
  const [durationAmpm, setDurationAmpm] = useState<'AM' | 'PM'>('AM');
  const [sleepHours, setSleepHours] = useState(7);
  const [sleepMinutes, setSleepMinutes] = useState(30);
  const [durationResult, setDurationResult] = useState<{time: string, message: string} | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    lightBlue: '#f0f9ff',
    skyBlue: '#e0f2fe',
  };

  const formatTime = (hours: number, minutes: number): string => {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  const parseTime = (hour: number, minute: number, ampm: 'AM' | 'PM'): {hours: number, minutes: number} => {
    let hours = hour;
    const minutes = minute;

    if (ampm === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  };

  const addMinutes = (hours: number, minutes: number, minutesToAdd: number): {hours: number, minutes: number} => {
    let totalMinutes = hours * 60 + minutes + minutesToAdd;

    while (totalMinutes < 0) {
      totalMinutes += 1440;
    }
    totalMinutes = totalMinutes % 1440;

    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60
    };
  };

  const setCurrentTime = (setHour: (h: number) => void, setMin: (m: number) => void, setAmpm: (a: 'AM' | 'PM') => void, offset: number = 0) => {
    const now = new Date();
    const totalMinutes = now.getHours() * 60 + now.getMinutes() + offset;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    setHour(hours % 12 || 12);
    setMin(minutes);
    setAmpm(hours >= 12 ? 'PM' : 'AM');
  };

  const handleCycleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const time = parseTime(cycleHour, cycleMin, cycleAmpm);
    const cycleMinutes = cycleLength;
    const fallAsleepMinutes = fallAsleep;

    const times = [];
    for (let cycles = 6; cycles >= 4; cycles--) {
      let totalMinutes = cycles * cycleMinutes + fallAsleepMinutes;
      let resultTime;

      if (cycleMode === 'wakeup') {
        resultTime = addMinutes(time.hours, time.minutes, -totalMinutes);
      } else {
        resultTime = addMinutes(time.hours, time.minutes, totalMinutes);
      }

      times.push({
        cycles: cycles,
        time: formatTime(resultTime.hours, resultTime.minutes),
        recommended: cycles === 5 || cycles === 6
      });
    }

    setCycleResults(times);
  };

  const handleDurationCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const time = parseTime(durationHour, durationMin, durationAmpm);
    const totalSleepMinutes = sleepHours * 60 + sleepMinutes;

    let resultTime;
    if (durationMode === 'wakeup') {
      resultTime = addMinutes(time.hours, time.minutes, -totalSleepMinutes);
    } else {
      resultTime = addMinutes(time.hours, time.minutes, totalSleepMinutes);
    }

    const totalHours = sleepHours + sleepMinutes / 60;
    let message = '';
    if (totalHours >= 7 && totalHours <= 9) {
      message = t('durationMessageGood');
    } else if (totalHours < 7) {
      message = t('durationMessageShort');
    } else {
      message = t('durationMessageLong');
    }

    setDurationResult({
      time: formatTime(resultTime.hours, resultTime.minutes),
      message
    });
  };

  const styles = {
    container: getResponsiveStyle(
      {
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative' as const,
        zIndex: 1,
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),

    header: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        marginBottom: '2rem',
      },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),

    h1: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.textPrimary,
        marginBottom: '1rem',
        lineHeight: 1.2,
      },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),

    summaryText: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.6,
        maxWidth: '800px',
        margin: '0 auto',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),

    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 2rem' }
    ),

    toolContainer: {
      maxWidth: '900px',
      margin: '0 auto',
    },

    toolHeader: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        marginBottom: '2rem',
      },
      {},
      { marginBottom: '2.5rem' },
      {}
    ),

    toolTitle: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.textPrimary,
        marginBottom: '0.5rem',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),

    calculatorCards: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr',
      gap: '2rem',
      marginBottom: '2rem',
    },

    calcCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),

    cardTitle: getResponsiveStyle(
      {
        color: colors.primary,
        marginBottom: '1.5rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),

    formGroup: {
      marginBottom: '1.5rem',
    },

    label: getResponsiveStyle(
      {
        display: 'block',
        fontWeight: 500,
        color: colors.textSecondary,
        marginBottom: '0.5rem',
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),

    radioGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem',
    },

    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },

    radioInput: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
      accentColor: colors.primary,
    },

    timeInputGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
    },

    timeInput: getResponsiveStyle(
      {
        width: '70px',
        padding: '0.625rem',
        border: `2px solid ${colors.glassBorder}`,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
      },
      {},
      {},
      {}
    ),

    select: getResponsiveStyle(
      {
        padding: '0.625rem 1rem',
        border: `2px solid ${colors.glassBorder}`,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        cursor: 'pointer',
        background: 'white',
        transition: 'all 0.3s ease',
      },
      {},
      {},
      {}
    ),

    timeSeparator: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#64748b',
    },

    quickActions: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
      marginTop: '0.75rem',
    },

    quickBtn: getResponsiveStyle(
      {
        padding: '0.375rem 0.75rem',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: '0.375rem',
        color: colors.textSecondary,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      { fontSize: '0.75rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.875rem' }
    ),

    durationInputs: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap' as const,
    },

    durationInput: {
      flex: 1,
      minWidth: '120px',
    },

    durationInputField: {
      width: '100%',
      padding: '0.625rem',
      border: `2px solid ${colors.glassBorder}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
    },

    settingsToggle: {
      marginTop: '1rem',
      textAlign: 'center' as const,
    },

    settingsLink: getResponsiveStyle(
      {
        color: colors.primary,
        textDecoration: 'none',
        fontWeight: 500,
        cursor: 'pointer',
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),

    settingsPanel: getResponsiveStyle(
      {
        background: '#f8fafc',
        borderRadius: '0.75rem',
        marginTop: '1rem',
        border: '2px solid #e2e8f0',
      },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),

    settingsPanelTitle: getResponsiveStyle(
      {
        marginTop: 0,
        marginBottom: '1rem',
        color: colors.textPrimary,
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),

    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
      },
      { padding: '0.75rem 1.25rem', fontSize: '1rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1.05rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1.05rem' }
    ),

    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),

    resultsTitle: getResponsiveStyle(
      {
        color: colors.textPrimary,
        marginBottom: '1.5rem',
        fontWeight: 600,
        textAlign: 'center' as const,
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),

    sleepTimes: {
      display: 'grid',
      gridTemplateColumns: screenSize === 'mobile' ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },

    sleepTimeCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        border: '2px solid #bae6fd',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.25rem' }
    ),

    sleepTimeCardRecommended: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderColor: colors.accent,
    },

    cycleLabel: getResponsiveStyle(
      {
        color: '#64748b',
        marginBottom: '0.5rem',
        fontWeight: 500,
      },
      { fontSize: '0.75rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.875rem' }
    ),

    timeValue: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.textPrimary,
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),

    recommendedBadge: getResponsiveStyle(
      {
        display: 'inline-block',
        background: colors.accent,
        color: 'white',
        padding: '0.25rem 0.75rem',
        borderRadius: '1rem',
        fontWeight: 600,
        marginTop: '0.5rem',
      },
      { fontSize: '0.625rem' },
      { fontSize: '0.75rem' },
      { fontSize: '0.75rem' }
    ),

    infoBox: getResponsiveStyle(
      {
        background: '#f0fdf4',
        borderLeft: '4px solid #22c55e',
        borderRadius: '0.5rem',
        marginTop: '1.5rem',
      },
      { padding: '0.875rem 1rem' },
      { padding: '1rem 1.25rem' },
      { padding: '1rem 1.25rem' }
    ),

    infoBoxText: getResponsiveStyle(
      {
        margin: 0,
        color: '#166534',
        lineHeight: 1.6,
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),

    ageRecommendations: getResponsiveStyle(
      {
        background: '#fefce8',
        borderRadius: '0.75rem',
        marginTop: '2rem',
      },
      { padding: '1.25rem' },
      { padding: '1.5rem' },
      { padding: '1.5rem' }
    ),

    ageRecommendationsTitle: getResponsiveStyle(
      {
        color: '#854d0e',
        marginTop: 0,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.1rem' }
    ),

    ageTable: getResponsiveStyle(
      {
        width: '100%',
        borderCollapse: 'collapse' as const,
      },
      { fontSize: '0.8rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),

    ageTableTh: getResponsiveStyle(
      {
        background: '#fef3c7',
        color: '#854d0e',
        fontWeight: 600,
        textAlign: 'left' as const,
        borderBottom: '1px solid #fde68a',
      },
      { padding: '0.5rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),

    ageTableTd: getResponsiveStyle(
      {
        textAlign: 'left' as const,
        borderBottom: '1px solid #fde68a',
      },
      { padding: '0.5rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),

    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),

    h2: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '1rem',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),

    paragraph: getResponsiveStyle(
      {
        lineHeight: 1.7,
        color: colors.textSecondary,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),

    list: getResponsiveStyle(
      {
        lineHeight: 1.8,
        color: colors.textSecondary,
        paddingLeft: '1.5rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),

    listItem: {
      marginBottom: '0.75rem',
    },

    strong: {
      color: colors.textPrimary,
      fontWeight: 600,
    },

    faqItem: {
      marginBottom: '1.5rem',
    },

    faqQuestion: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.5rem',
      },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),

    faqAnswer: getResponsiveStyle(
      {
        lineHeight: 1.7,
        color: colors.textSecondary,
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),

    referenceItem: getResponsiveStyle(
      {
        lineHeight: 1.6,
        color: colors.textSecondary,
        marginBottom: '0.5rem',
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),

    link: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolTitle}>{t("toolTitle")}</h2>
          </div>

          <div style={styles.calculatorCards}>
            <div style={styles.calcCard}>
              <h3 style={styles.cardTitle}>🌙 {t("cycleCalculatorTitle")}</h3>
              <form onSubmit={handleCycleCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("iWantTo")}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioOption}>
                      <input
                        type="radio"
                        name="cycle-mode"
                        value="wakeup"
                        checked={cycleMode === 'wakeup'}
                        onChange={() => setCycleMode('wakeup')}
                        style={styles.radioInput}
                      />
                      <span>{t("wakeUpAt")}</span>
                    </label>
                    <label style={styles.radioOption}>
                      <input
                        type="radio"
                        name="cycle-mode"
                        value="bedtime"
                        checked={cycleMode === 'bedtime'}
                        onChange={() => setCycleMode('bedtime')}
                        style={styles.radioInput}
                      />
                      <span>{t("goToBedAt")}</span>
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={cycleHour}
                      onChange={(e) => setCycleHour(parseInt(e.target.value) || 1)}
                      required
                      style={styles.timeInput}
                    />
                    <span style={styles.timeSeparator}>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={cycleMin}
                      onChange={(e) => setCycleMin(parseInt(e.target.value) || 0)}
                      required
                      style={styles.timeInput}
                    />
                    <select
                      value={cycleAmpm}
                      onChange={(e) => setCycleAmpm(e.target.value as 'AM' | 'PM')}
                      style={styles.select}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  {cycleMode === 'bedtime' && (
                    <div style={styles.quickActions}>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setCycleHour, setCycleMin, setCycleAmpm, 0)}
                      >
                        {t("now")}
                      </button>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setCycleHour, setCycleMin, setCycleAmpm, 30)}
                      >
                        {t("thirtyMinLater")}
                      </button>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setCycleHour, setCycleMin, setCycleAmpm, 60)}
                      >
                        {t("oneHourLater")}
                      </button>
                    </div>
                  )}
                </div>

                {showCycleSettings && (
                  <div style={styles.settingsPanel}>
                    <h4 style={styles.settingsPanelTitle}>⚙️ {t("settings")}</h4>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("cycleLengthLabel")}</label>
                      <input
                        type="number"
                        value={cycleLength}
                        onChange={(e) => setCycleLength(parseInt(e.target.value) || 90)}
                        min="60"
                        max="120"
                        style={styles.durationInputField}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("fallAsleepLabel")}</label>
                      <input
                        type="number"
                        value={fallAsleep}
                        onChange={(e) => setFallAsleep(parseInt(e.target.value) || 15)}
                        min="0"
                        max="60"
                        style={styles.durationInputField}
                      />
                    </div>
                  </div>
                )}

                <div style={styles.settingsToggle}>
                  <a
                    style={styles.settingsLink}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowCycleSettings(!showCycleSettings);
                    }}
                  >
                    ⚙️ {t("settings")}
                  </a>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateSleepTimes")}
                </button>
              </form>
            </div>

            <div style={styles.calcCard}>
              <h3 style={styles.cardTitle}>⏰ {t("durationCalculatorTitle")}</h3>
              <form onSubmit={handleDurationCalculate}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("iWantTo")}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioOption}>
                      <input
                        type="radio"
                        name="duration-mode"
                        value="wakeup"
                        checked={durationMode === 'wakeup'}
                        onChange={() => setDurationMode('wakeup')}
                        style={styles.radioInput}
                      />
                      <span>{t("wakeUpAt")}</span>
                    </label>
                    <label style={styles.radioOption}>
                      <input
                        type="radio"
                        name="duration-mode"
                        value="bedtime"
                        checked={durationMode === 'bedtime'}
                        onChange={() => setDurationMode('bedtime')}
                        style={styles.radioInput}
                      />
                      <span>{t("goToBedAt")}</span>
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={durationHour}
                      onChange={(e) => setDurationHour(parseInt(e.target.value) || 1)}
                      required
                      style={styles.timeInput}
                    />
                    <span style={styles.timeSeparator}>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={durationMin}
                      onChange={(e) => setDurationMin(parseInt(e.target.value) || 0)}
                      required
                      style={styles.timeInput}
                    />
                    <select
                      value={durationAmpm}
                      onChange={(e) => setDurationAmpm(e.target.value as 'AM' | 'PM')}
                      style={styles.select}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  {durationMode === 'bedtime' && (
                    <div style={styles.quickActions}>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setDurationHour, setDurationMin, setDurationAmpm, 0)}
                      >
                        {t("now")}
                      </button>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setDurationHour, setDurationMin, setDurationAmpm, 30)}
                      >
                        {t("thirtyMinLater")}
                      </button>
                      <button
                        type="button"
                        style={styles.quickBtn}
                        onClick={() => setCurrentTime(setDurationHour, setDurationMin, setDurationAmpm, 60)}
                      >
                        {t("oneHourLater")}
                      </button>
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t("iWantToSleepFor")}</label>
                  <div style={styles.durationInputs}>
                    <div style={styles.durationInput}>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        value={sleepHours}
                        onChange={(e) => setSleepHours(parseInt(e.target.value) || 0)}
                        required
                        style={styles.durationInputField}
                      />
                      <label style={{...styles.label, marginTop: '0.25rem', fontSize: '0.875rem'}}>{t("hours")}</label>
                    </div>
                    <div style={styles.durationInput}>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={sleepMinutes}
                        onChange={(e) => setSleepMinutes(parseInt(e.target.value) || 0)}
                        required
                        style={styles.durationInputField}
                      />
                      <label style={{...styles.label, marginTop: '0.25rem', fontSize: '0.875rem'}}>{t("minutes")}</label>
                    </div>
                  </div>
                </div>

                <button type="submit" style={styles.calculateBtn}>
                  {t("calculateTime")}
                </button>
              </form>
            </div>
          </div>

          {cycleResults && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("recommendedSleepWakeTimes")}</h3>
              <div style={styles.sleepTimes}>
                {cycleResults.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.sleepTimeCard,
                      ...(item.recommended ? styles.sleepTimeCardRecommended : {})
                    }}
                  >
                    <div style={styles.cycleLabel}>{item.cycles} {t("cycles")}</div>
                    <div style={styles.timeValue}>{item.time}</div>
                    {item.recommended && (
                      <div style={styles.recommendedBadge}>{t("recommended")}</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={styles.infoBox}>
                <p style={styles.infoBoxText}>
                  <strong>💡 {t("tip")}:</strong> {t("cycleResultsTip")}
                </p>
              </div>
            </div>
          )}

          {durationResult && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("yourSleepSchedule")}</h3>
              <div style={styles.sleepTimes}>
                <div style={{...styles.sleepTimeCard, ...styles.sleepTimeCardRecommended}}>
                  <div style={styles.cycleLabel}>
                    {durationMode === 'wakeup' ? t("goToBedAt") : t("wakeUpAt")}
                  </div>
                  <div style={styles.timeValue}>{durationResult.time}</div>
                  <div style={styles.recommendedBadge}>
                    {sleepHours}{t("hoursShort")} {sleepMinutes}{t("minutesShort")} {t("sleep")}
                  </div>
                </div>
              </div>
              <div style={styles.infoBox}>
                <p style={styles.infoBoxText} dangerouslySetInnerHTML={{ __html: durationResult.message }} />
              </div>
            </div>
          )}

          <div style={styles.ageRecommendations}>
            <h4 style={styles.ageRecommendationsTitle}>{t("ageRecommendationsTitle")}</h4>
            <table style={styles.ageTable}>
              <thead>
                <tr>
                  <th style={styles.ageTableTh}>{t("ageGroup")}</th>
                  <th style={styles.ageTableTh}>{t("recommendedHours")}</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={styles.ageTableTd}>{t("age0to3")}</td><td style={styles.ageTableTd}>{t("hours14to17")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age4to12")}</td><td style={styles.ageTableTd}>{t("hours12to16")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age1to2")}</td><td style={styles.ageTableTd}>{t("hours11to14")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age3to5")}</td><td style={styles.ageTableTd}>{t("hours10to13")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age6to12")}</td><td style={styles.ageTableTd}>{t("hours9to12")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age13to18")}</td><td style={styles.ageTableTd}>{t("hours8to10")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age18to60")}</td><td style={styles.ageTableTd}>{t("hours7plus")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age61to64")}</td><td style={styles.ageTableTd}>{t("hours7to9")}</td></tr>
                <tr><td style={styles.ageTableTd}>{t("age65plus")}</td><td style={styles.ageTableTd}>{t("hours7to8")}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t("step1Title")}:</strong> {t("step1Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("step2Title")}:</strong> {t("step2Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("step3Title")}:</strong> {t("step3Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("step4Title")}:</strong> {t("step4Description")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t("sleepCyclesTitle")}</h3>
        <p style={styles.paragraph}>{t("sleepCyclesParagraph")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t("healthImpactTitle")}</h3>
        <p style={styles.paragraph}>{t("healthImpactParagraph")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>{t("evidenceTitle")}</h3>
        <p style={styles.paragraph}>{t("evidenceParagraph")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("bestPracticesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice1Title")}:</strong> {t("practice1Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice2Title")}:</strong> {t("practice2Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice3Title")}:</strong> {t("practice3Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice4Title")}:</strong> {t("practice4Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice5Title")}:</strong> {t("practice5Description")}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t("practice6Title")}:</strong> {t("practice6Description")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.referenceItem}>
            <a href="https://www.sleepfoundation.org/how-sleep-works/sleep-cycles" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t("reference1")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.cdc.gov/sleep/about_sleep/how_much_sleep.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t("reference2")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://aasm.org/resources/factsheets/sleepapnea.pdf" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t("reference3")}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <a href="https://www.sleephealthfoundation.org.au/sleep-topics/sleep-calculators" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t("reference4")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}